import { ThemeEvents } from '@theme/events';

class EcomMindsSeasonProgress extends HTMLElement {
  static productCache = new Map();

  connectedCallback() {
    this.thresholds = [+this.dataset.t1, +this.dataset.t2, +this.dataset.t3];
    this.discounts = [+this.dataset.d1, +this.dataset.d2, +this.dataset.d3];
    this.campaignTag = (this.dataset.campaignTag || 'Yeni Sezon').trim().toLocaleLowerCase('tr-TR');
    this.abortController = new AbortController();
    this.renderToken = 0;

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate, {
      signal: this.abortController.signal,
    });

    this.position();
    this.loadCart();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  position() {
    const max = this.thresholds[2] || 1;
    this.thresholds.forEach((value, index) => {
      const x = `${Math.max(0, Math.min(100, (value / max) * 100))}%`;
      this.querySelector(`[data-m="${index + 1}"]`)?.style.setProperty('--x', x);
      this.querySelector(`[data-l="${index + 1}"]`)?.style.setProperty('--x', x);
    });
  }

  onCartUpdate = (event) => {
    const cart = event.detail?.resource;

    // The EcomMinds drawer's own event currently has no resource, so refetch in that case.
    if (cart?.items) {
      this.updateFromCart(cart);
    } else {
      this.loadCart();
    }
  };

  async loadCart() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      await this.updateFromCart(await response.json());
    } catch (error) {
      console.error('[EcomMinds Season Progress] cart load failed', error);
    }
  }

  async updateFromCart(cart) {
    const token = ++this.renderToken;
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const eligibleFlags = await Promise.all(
      items.map((item) => this.isEligible(item))
    );

    if (token !== this.renderToken) return;

    // IMPORTANT: original_line_price is used so an already-applied discount
    // does not reduce the progress amount and make the bar fall backwards.
    const eligibleTotal = items.reduce((sum, item, index) => {
      if (!eligibleFlags[index]) return sum;
      return sum + Number(item.original_line_price ?? item.final_line_price ?? 0);
    }, 0);

    this.render(eligibleTotal);
  }

  async isEligible(item) {
    const handle = item?.handle;
    if (!handle) return false;

    const cacheKey = `${handle}::${this.campaignTag}`;
    if (EcomMindsSeasonProgress.productCache.has(cacheKey)) {
      return EcomMindsSeasonProgress.productCache.get(cacheKey);
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

      const product = await response.json();
      const tags = Array.isArray(product.tags)
        ? product.tags
        : String(product.tags || '').split(',');

      const eligible = tags.some(
        (tag) => String(tag).trim().toLocaleLowerCase('tr-TR') === this.campaignTag
      );

      EcomMindsSeasonProgress.productCache.set(cacheKey, eligible);
      return eligible;
    } catch (error) {
      console.error('[EcomMinds Season Progress] product eligibility failed', handle, error);
      EcomMindsSeasonProgress.productCache.set(cacheKey, false);
      return false;
    }
  }

  money(cents) {
    const currency = window.Shopify?.currency?.active || 'TRY';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format((Number(cents) || 0) / 100);
    } catch {
      return `${((Number(cents) || 0) / 100).toFixed(0)} ${currency}`;
    }
  }

  render(total) {
    total = Math.max(0, Number(total) || 0);
    const max = this.thresholds[2] || 1;
    const fill = this.querySelector('[data-fill]');
    if (fill) fill.style.width = `${Math.min(100, (total / max) * 100)}%`;

    const totalNode = this.querySelector('[data-eligible-total]');
    if (totalNode) totalNode.textContent = this.money(total);

    let earned = -1;
    let next = -1;

    this.thresholds.forEach((threshold, index) => {
      const reached = total >= threshold;
      if (reached) earned = index;
      else if (next < 0) next = index;

      this.querySelector(`[data-m="${index + 1}"]`)?.classList.toggle('hit', reached);
      this.querySelector(`[data-l="${index + 1}"]`)?.classList.toggle('hit', reached);
    });

    const message = this.querySelector('[data-msg]');
    if (!message) return;

    if (next < 0) {
      message.innerHTML = `Tebrikler • <strong>+%${this.discounts[2]} indirim</strong> seviyesine ulaştın`;
      return;
    }

    const remaining = Math.max(0, this.thresholds[next] - total);

    if (earned < 0) {
      message.innerHTML = `<strong>${this.money(remaining)}</strong> daha Yeni Sezon ürünü ekle, +%${this.discounts[next]} indirime ulaş`;
    } else {
      message.innerHTML =
        `<strong>+%${this.discounts[earned]} indirimi kazandın</strong> • ` +
        `${this.money(remaining)} daha Yeni Sezon ürünü ekle, +%${this.discounts[next]}'e ulaş`;
    }
  }
}

if (!customElements.get('ecomminds-season-progress')) {
  customElements.define('ecomminds-season-progress', EcomMindsSeasonProgress);
}
